import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Transform NamUs photo URLs to use our proxy endpoint.
 * NamUs API requires session cookies, so we proxy through our server.
 * 
 * @param {string} url - The photo URL to transform
 * @returns {string} - Proxied URL if NamUs, otherwise original URL
 */
export const getProxiedPhotoUrl = (url) => {
  if (!url) return null;
  
  // Check if this is a NamUs API URL
  if (url.includes('namus.gov/api/CaseSets/NamUs/')) {
    // Extract case type and ID from URL
    // URL format: https://www.namus.gov/api/CaseSets/NamUs/{MissingPersons|UnidentifiedPersons}/Cases/{id}/Images/...
    const match = url.match(/CaseSets\/NamUs\/(MissingPersons|UnidentifiedPersons)\/Cases\/([^\/]+)\/Images(?:\/(\d+))?/);
    
    if (match) {
      const [, caseType, caseId, imageId] = match;
      let proxyUrl = `${API}/photos/namus/proxy?case_type=${caseType}&case_id=${caseId}`;
      if (imageId) {
        proxyUrl += `&image_id=${imageId}`;
      }
      return proxyUrl;
    }
  }
  
  // Return original URL for non-NamUs photos
  return url;
};

// Public API instance (no credentials needed for public endpoints)
const publicApi = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Authenticated API instance (includes credentials for session-based auth)
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies for authentication
  // Hard upper bound on any request — protects the UI from a stuck
  // backend worker. If the backend ever wedges (e.g. an asyncio loop
  // pegged at 100% so individual requests never get scheduled), the
  // spinner now resolves to a clear `timeout` toast within 45s
  // rather than spinning forever. Long-running PDF / digest endpoints
  // override this locally via `{ timeout: <ms> }`.
  timeout: 45000,
});

// Add request interceptor to include token from localStorage as fallback
api.interceptors.request.use(
  config => {
    // Add Authorization header from localStorage if token exists
    // This provides a fallback when cookies aren't sent (e.g., cross-origin issues)
    const token = localStorage.getItem('token');
    if (token && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  response => response,
  error => {
    // If there's a CORS error with credentials, fall back to public API
    if (error.message?.includes('CORS') || error.code === 'ERR_NETWORK') {
      console.warn('CORS error with credentials, request may need public API');
    }
    // Surface a request timeout as a distinct, actionable message.
    // axios uses `error.code === 'ECONNABORTED'` for timeouts.
    if (error.code === 'ECONNABORTED') {
      console.warn(
        `Request timed out after ${error.config?.timeout}ms: `
        + `${error.config?.method?.toUpperCase()} ${error.config?.url}`
      );
    }
    return Promise.reject(error);
  }
);

// Cases (public endpoints)
export const getCases = async (params = {}) => {
  const response = await publicApi.get('/cases', { params });
  return response.data;
};

export const getCase = async (id) => {
  const response = await publicApi.get(`/cases/${id}`);
  return response.data;
};

// Unified endpoint to get any case from any collection (missing_persons, external_cases, canonical_cases)
export const getAnyCase = async (id) => {
  const response = await publicApi.get(`/any_case/${id}`);
  return response.data;
};

export const createCase = async (caseData) => {
  const response = await api.post('/cases', caseData);
  return response.data;
};

export const updateCaseStatus = async (id, status) => {
  const response = await api.patch(`/cases/${id}/status?status=${status}`);
  return response.data;
};

// Case Review (admin endpoints)
export const getPendingCases = async (params = {}) => {
  const response = await api.get('/cases/review/pending', { params });
  return response.data;
};

export const getReviewStats = async () => {
  const response = await api.get('/cases/review/stats');
  return response.data;
};

export const approveCase = async (caseId, notes = null) => {
  const params = notes ? `?notes=${encodeURIComponent(notes)}` : '';
  const response = await api.post(`/cases/${caseId}/approve${params}`);
  return response.data;
};

export const rejectCase = async (caseId, reason) => {
  const response = await api.post(`/cases/${caseId}/reject?reason=${encodeURIComponent(reason)}`);
  return response.data;
};

export const getMySubmissions = async () => {
  const response = await api.get('/cases/my-submissions');
  return response.data;
};

export const getStats = async () => {
  const response = await publicApi.get('/cases/stats/summary');
  return response.data;
};

// Homepage endpoints with caching
const homepageCache = {
  featured: { data: null, expiry: 0 },
  recent: { data: null, expiry: 0 }
};

export const getFeaturedCases = async (limit = 16) => {
  const now = Date.now();
  if (homepageCache.featured.data && homepageCache.featured.expiry > now) {
    return homepageCache.featured.data;
  }
  const response = await publicApi.get('/home/featured', { params: { limit } });
  // Cache for 5 minutes
  homepageCache.featured = { data: response.data, expiry: now + 5 * 60 * 1000 };
  return response.data;
};

export const getRecentCases = async (limit = 6, requirePhoto = false, types = 'missing,unidentified') => {
  const cacheKey = `recent_${limit}_${requirePhoto}_${types}`;
  const now = Date.now();
  if (homepageCache.recent.data && homepageCache.recent.expiry > now) {
    return homepageCache.recent.data;
  }
  const response = await publicApi.get('/home/recent', { 
    params: { limit, require_photo: requirePhoto, types } 
  });
  // Cache for 5 minutes
  homepageCache.recent = { data: response.data, expiry: now + 5 * 60 * 1000 };
  return response.data;
};

export const updateCaseFeaturedStatus = async (caseId, isFeatured, featuredRank = null, featuredUntil = null) => {
  const params = { is_featured: isFeatured };
  if (featuredRank !== null) params.featured_rank = featuredRank;
  if (featuredUntil) params.featured_until = featuredUntil;
  const response = await api.put(`/home/featured/${caseId}`, null, { params });
  return response.data;
};

// Tips
export const submitTip = async (tipData, captchaToken = null) => {
  const payload = { ...tipData };
  if (captchaToken) {
    payload.captcha_token = captchaToken;
  }
  const response = await api.post('/tips', payload);
  return response.data;
};

export const getTipsForCase = async (caseId) => {
  const response = await api.get(`/tips/${caseId}`);
  return response.data;
};

// Alerts
export const subscribeToAlerts = async (subscriptionData) => {
  const response = await api.post('/alerts/subscribe', subscriptionData);
  return response.data;
};

export const getRecentAlerts = async (limit = 10) => {
  const response = await api.get('/alerts/recent', { params: { limit } });
  return response.data;
};

// Subscription Management
export const getMySubscription = async () => {
  const response = await api.get('/subscriptions/my-subscription');
  return response.data;
};

export const cancelSubscription = async () => {
  const response = await api.post('/subscriptions/cancel');
  return response.data;
};

export const reactivateSubscription = async () => {
  const response = await api.post('/subscriptions/reactivate');
  return response.data;
};

// Featured Case Credits
export const getFeaturedCredits = async () => {
  const response = await api.get('/users/featured-credits');
  return response.data;
};

export const boostCaseToFeatured = async (caseId) => {
  const response = await api.post(`/cases/${caseId}/boost`);
  return response.data;
};

export const removeBoostFromCase = async (caseId) => {
  const response = await api.delete(`/cases/${caseId}/boost`);
  return response.data;
};

export const getUserFeaturedCases = async () => {
  const response = await api.get('/users/featured-cases');
  return response.data;
};

// ==================== FAMILYSEARCH / GENEALOGY ====================

export const getFamilySearchStatus = async () => {
  const response = await api.get('/familysearch/status');
  return response.data;
};

export const getFamilySearchUser = async () => {
  const response = await api.get('/familysearch/auth/me');
  return response.data;
};

export const searchFamilySearchRecords = async (searchParams) => {
  const response = await api.post('/familysearch/search/records', searchParams);
  return response.data;
};

export const searchFamilySearchTree = async (searchParams) => {
  const response = await api.post('/familysearch/search/tree', searchParams);
  return response.data;
};

export const getFamilySearchPerson = async (personId) => {
  const response = await api.get(`/familysearch/person/${personId}`);
  return response.data;
};

export const linkGenealogyToCase = async (linkData) => {
  const response = await api.post('/familysearch/link', linkData);
  return response.data;
};

export const getCaseGenealogyLinks = async (caseId) => {
  const response = await api.get(`/familysearch/case/${caseId}/links`);
  return response.data;
};

export const removeGenealogyLink = async (linkId) => {
  const response = await api.delete(`/familysearch/link/${linkId}`);
  return response.data;
};

// ==================== WORKSPACE (PREMIUM FEATURES) ====================

// Quality Contribution Stats (Professional/Organization only)
export const getQualityContributionStats = async () => {
  const response = await api.get('/matches/feedback/quality-contribution');
  return response.data;
};

// Workspace Summary
export const getWorkspaceSummary = async () => {
  const response = await api.get('/workspace/summary');
  return response.data;
};

// Saved Cases (Watchlist)
export const getSavedCases = async (folder = null, limit = 50, offset = 0) => {
  const params = { limit, offset };
  if (folder) params.folder = folder;
  const response = await api.get('/workspace/saved-cases', { params });
  return response.data;
};

// Save a case - supports both formats: saveCase(caseData) or saveCase(caseId, caseData)
export const saveCase = async (caseIdOrData, caseData = {}) => {
  // Handle both call signatures for backwards compatibility
  let payload;
  if (typeof caseIdOrData === 'string') {
    // New format: saveCase(caseId, caseData)
    payload = {
      case_id: caseIdOrData,
      case_type: caseData.case_type,
      case_name: caseData.name || caseData.case_name,
      case_photo_url: caseData.thumbnail_url || caseData.case_photo_url
    };
  } else {
    // Old format: saveCase(caseData)
    payload = caseIdOrData;
  }
  const response = await api.post('/workspace/saved-cases', payload);
  return response.data;
};

export const unsaveCase = async (caseId) => {
  const response = await api.delete(`/workspace/saved-cases/${caseId}`);
  return response.data;
};

export const updateSavedCase = async (caseId, updates) => {
  const response = await api.put(`/workspace/saved-cases/${caseId}`, null, { params: updates });
  return response.data;
};

// Check if a case is saved (alias for consistency)
export const checkCaseSaved = async (caseId) => {
  const response = await api.get(`/workspace/saved-cases/check/${caseId}`);
  return response.data;
};

// Alias for SaveCaseButton compatibility
export const checkIfCaseSaved = checkCaseSaved;

// Saved Matches
export const getSavedMatches = async (status = null, limit = 50, offset = 0) => {
  const params = { limit, offset };
  if (status) params.status = status;
  const response = await api.get('/workspace/saved-matches', { params });
  return response.data;
};

export const saveMatch = async (matchData) => {
  const response = await api.post('/workspace/saved-matches', matchData);
  return response.data;
};

export const updateSavedMatch = async (matchId, updates) => {
  const response = await api.put(`/workspace/saved-matches/${matchId}`, updates);
  return response.data;
};

export const deleteSavedMatch = async (matchId) => {
  const response = await api.delete(`/workspace/saved-matches/${matchId}`);
  return response.data;
};

// Saved Searches
export const getSavedSearches = async (searchType = null) => {
  const params = {};
  if (searchType) params.search_type = searchType;
  const response = await api.get('/workspace/saved-searches', { params });
  return response.data;
};

export const saveSearch = async (searchData) => {
  const response = await api.post('/workspace/saved-searches', searchData);
  return response.data;
};

export const updateSavedSearch = async (searchId, updates) => {
  const response = await api.put(`/workspace/saved-searches/${searchId}`, null, { params: updates });
  return response.data;
};

export const runSavedSearch = async (searchId) => {
  const response = await api.post(`/workspace/saved-searches/${searchId}/run`);
  return response.data;
};

export const deleteSavedSearch = async (searchId) => {
  const response = await api.delete(`/workspace/saved-searches/${searchId}`);
  return response.data;
};

// Case Notes
export const getAllNotes = async (limit = 50, offset = 0) => {
  const response = await api.get('/workspace/notes', { params: { limit, offset } });
  return response.data;
};

export const getCaseNotes = async (caseId) => {
  const response = await api.get(`/workspace/notes/case/${caseId}`);
  return response.data;
};

export const createNote = async (noteData) => {
  const response = await api.post('/workspace/notes', noteData);
  return response.data;
};

export const updateNote = async (noteId, updates) => {
  const response = await api.put(`/workspace/notes/${noteId}`, null, { params: updates });
  return response.data;
};

export const deleteNote = async (noteId) => {
  const response = await api.delete(`/workspace/notes/${noteId}`);
  return response.data;
};

// Activity History
export const getActivityHistory = async (activityType = null, limit = 50, offset = 0) => {
  const params = { limit, offset };
  if (activityType) params.activity_type = activityType;
  const response = await api.get('/workspace/activity', { params });
  return response.data;
};

export const clearActivityHistory = async () => {
  const response = await api.delete('/workspace/activity');
  return response.data;
};

// Facial Comparisons
export const getSavedFacialComparisons = async (reviewStatus = null, includeHidden = false, limit = 50, offset = 0) => {
  const params = { limit, offset, include_hidden: includeHidden };
  if (reviewStatus) params.review_status = reviewStatus;
  const response = await api.get('/workspace/facial-comparisons', { params });
  return response.data;
};

export const saveFacialComparison = async (comparisonData) => {
  const response = await api.post('/workspace/facial-comparisons', comparisonData);
  return response.data;
};

export const updateFacialComparison = async (comparisonId, updates) => {
  const response = await api.put(`/workspace/facial-comparisons/${comparisonId}`, updates);
  return response.data;
};

export const hideFacialComparison = async (comparisonId) => {
  const response = await api.delete(`/workspace/facial-comparisons/${comparisonId}`);
  return response.data;
};

export const checkFacialComparisonSaved = async (referenceCaseId, matchedCaseId) => {
  const response = await api.get('/workspace/facial-comparisons/check', {
    params: { reference_case_id: referenceCaseId, matched_case_id: matchedCaseId }
  });
  return response.data;
};

// Bulk actions for facial comparisons
export const bulkUpdateFacialComparisons = async (ids, action) => {
  const response = await api.post('/workspace/facial-comparisons/bulk', { ids, action });
  return response.data;
};

export const undoBulkFacialAction = async (actionId) => {
  const response = await api.post(`/workspace/facial-comparisons/bulk/undo?action_id=${actionId}`);
  return response.data;
};

// Organizations
export const registerOrganization = async (orgData) => {
  const response = await api.post('/organizations', orgData);
  return response.data;
};

export const getOrganizations = async (verifiedOnly = false) => {
  const response = await api.get('/organizations', { params: { verified_only: verifiedOnly } });
  return response.data;
};

// AI Features
export const aiSearch = async (query) => {
  const response = await api.post('/ai/search', { query });
  return response.data;
};

export const smartSearch = async (query) => {
  const response = await publicApi.post('/ai/smart-search', { query });
  return response.data;
};

export const getAISummary = async (caseId) => {
  const response = await api.get(`/ai/summary/${caseId}`);
  return response.data;
};

export const findSimilarCases = async (description) => {
  const response = await api.post(`/ai/similarity?description=${encodeURIComponent(description)}`);
  return response.data;
};

export const findSimilarCasesEnhanced = async (caseId, limit = 20) => {
  const response = await publicApi.post(`/ai/find-similar/${caseId}?limit=${limit}`);
  return response.data;
};

// External Sources (public endpoints)
export const getExternalSources = async () => {
  const response = await publicApi.get('/external/sources');
  return response.data;
};

export const getExternalCases = async (params = {}) => {
  const response = await publicApi.get('/external/cases', { params });
  return response.data;
};

// Get cases near a geographic location
export const getNearbyCases = async (lat, lng, radiusMiles = 50, caseType = null, limit = 50) => {
  const params = { lat, lng, radius_miles: radiusMiles, limit };
  if (caseType) params.case_type = caseType;
  const response = await publicApi.get('/external/cases/nearby', { params });
  return response.data;
};

// Get lightweight map markers (optimized for map display)
export const getMapMarkers = async (limit = 5000, bounds = null, source = null) => {
  const params = { limit };
  if (bounds) params.bounds = bounds;
  if (source) params.source = source;
  const response = await publicApi.get('/external/map-markers', { params });
  return response.data;
};

// Browse page API - returns deduplicated cases (canonical + non-merged)
export const getBrowseCases = async (params = {}, retries = 2) => {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await publicApi.get('/browse/cases', { 
        params,
        timeout: 20000  // 20 second timeout
      });
      // Check if we got an error response from the server
      if (response.data?.error) {
        console.warn(`[getBrowseCases] Server returned error: ${response.data.error}`);
        if (attempt < retries) {
          console.log(`[getBrowseCases] Retrying... (attempt ${attempt + 2}/${retries + 1})`);
          await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
          continue;
        }
      }
      return response.data;
    } catch (error) {
      console.error(`[getBrowseCases] Request failed (attempt ${attempt + 1}/${retries + 1}):`, error.message);
      if (attempt < retries) {
        console.log(`[getBrowseCases] Retrying in ${(attempt + 1)}s...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
      } else {
        throw error;
      }
    }
  }
  return { cases: [], total: 0, has_more: false, error: 'Failed to load cases after retries' };
};

// Get canonical case with all source listings
export const getCanonicalCase = async (id) => {
  const response = await publicApi.get(`/canonical-cases/${id}`);
  return response.data;
};

export const getExternalCase = async (id) => {
  const response = await publicApi.get(`/external/cases/${id}`);
  return response.data;
};

export const createExternalCase = async (caseData) => {
  const response = await api.post('/external/cases', caseData);
  return response.data;
};

export const getExternalCaseLocation = async (caseId) => {
  const response = await publicApi.get(`/external/cases/${caseId}/location`);
  return response.data;
};

// Batch Geocoding API
export const startBatchGeocoding = async () => {
  const response = await api.post('/external/batch-geocode/start');
  return response.data;
};

export const stopBatchGeocoding = async () => {
  const response = await api.post('/external/batch-geocode/stop');
  return response.data;
};

export const getBatchGeocodingStatus = async () => {
  const response = await publicApi.get('/external/batch-geocode/status');
  return response.data;
};

export const updateExternalCase = async (id, caseData) => {
  const response = await api.put(`/external/cases/${id}`, caseData);
  return response.data;
};

export const deleteExternalCase = async (id) => {
  const response = await api.delete(`/external/cases/${id}`);
  return response.data;
};

export const getExternalStats = async () => {
  const response = await publicApi.get('/external/stats');
  return response.data;
};

export const getBrowseFacets = async (params = {}) => {
  const response = await publicApi.get('/browse/facets', { params });
  return response.data;
};

// NamUs Image Proxy - fetches images through our backend to handle CORS/auth issues
export const getNamusImageProxy = async (namusCaseId, imageId, size = 'Original') => {
  try {
    const response = await publicApi.get(`/external/namus-image/${namusCaseId}/${imageId}`, {
      params: { size },
      responseType: 'blob'
    });
    // Convert blob to data URL for direct use in img src
    const blob = response.data;
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve({ success: true, data: reader.result });
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('NamUs proxy error:', error);
    return { success: false, error: error.message };
  }
};

// FBI Image Proxy - fetches FBI images through our backend to bypass CORS restrictions
export const getFbiImageProxyUrl = (fbiImageUrl) => {
  return `${API}/external/fbi-image-proxy?url=${encodeURIComponent(fbiImageUrl)}`;
};

// Helper to extract NamUs IDs from URL
export const parseNamusImageUrl = (url) => {
  if (!url || !url.includes('namus.gov')) return null;
  
  // Extract case type (MissingPersons, UnidentifiedPersons, or UnclaimedPersons)
  let caseType = 'MissingPersons';
  if (url.includes('UnidentifiedPersons')) {
    caseType = 'UnidentifiedPersons';
  } else if (url.includes('UnclaimedPersons')) {
    caseType = 'UnclaimedPersons';
  }
  
  // Handle /Images/Primary or /Images/Default (with optional /Thumbnail suffix)
  // e.g., .../Cases/40614/Images/Primary or .../Cases/3155/Images/Default/Thumbnail
  const primaryMatch = url.match(/Cases\/([A-Za-z0-9]+)\/Images\/(Primary|Default)(\/Thumbnail)?/i);
  if (primaryMatch) {
    return { namusCaseId: primaryMatch[1], imageId: 'Default', caseType };
  }
  
  // Handle numeric image IDs (e.g., .../Cases/40614/Images/12345)
  const numericMatch = url.match(/Cases\/([A-Za-z0-9]+)\/Images\/(\d+)/);
  if (numericMatch) {
    return { namusCaseId: numericMatch[1], imageId: numericMatch[2], caseType };
  }
  
  return null;
};

// Get a direct URL to our NamUs image proxy
export const getNamusImageProxyUrl = (namusCaseId, imageId, caseType = 'MissingPersons') => {
  return `${API}/external/namus-image/${namusCaseId}/${imageId}?case_type=${caseType}`;
};

export const getQuickLinks = async () => {
  const response = await publicApi.get('/external/quick-links');
  return response.data;
};

export const fetchRSSFeeds = async () => {
  const response = await api.post('/external/fetch-rss');
  return response.data;
};

// NamUs Import
export const getNamusStats = async () => {
  const response = await publicApi.get('/external/namus-stats');
  return response.data;
};

export const importNamusCases = async (params = {}) => {
  const response = await api.post('/external/fetch-namus', params);
  return response.data;
};

// Duplicate Detection
export const scanForDuplicates = async (limit = 500) => {
  const response = await api.post('/duplicates/scan', null, { params: { limit } });
  return response.data;
};

export const getDuplicateCandidates = async (params = {}) => {
  const response = await api.get('/duplicates/candidates', { params });
  return response.data;
};

export const getDuplicateStats = async () => {
  const response = await api.get('/duplicates/stats');
  return response.data;
};

export const getDuplicatesForCase = async (caseId, minConfidence = 40) => {
  const response = await api.get(`/duplicates/for-case/${caseId}`, { params: { min_confidence: minConfidence } });
  return response.data;
};

export const mergeDuplicateCases = async (sourceListingIds, fieldOverrides = {}, reason = '') => {
  const response = await api.post('/duplicates/merge', {
    source_listing_ids: sourceListingIds,
    field_overrides: fieldOverrides,
    reason: reason
  });
  return response.data;
};

export const rejectDuplicateCandidate = async (candidateId, reason = '') => {
  const response = await api.post(`/duplicates/reject/${candidateId}`, null, { params: { reason } });
  return response.data;
};

// Legacy exports for backward compatibility
export const getDuplicateGroups = getDuplicateCandidates;
export const mergeDuplicates = async (groupId, primaryCaseId) => {
  // Convert old format to new
  return mergeDuplicateCases([primaryCaseId], {}, 'Merged from duplicate review UI');
};
export const dismissDuplicate = async (candidateId) => {
  return rejectDuplicateCandidate(candidateId, 'Dismissed from duplicate review UI');
};

// Organization Verification
export const requestOrgVerification = async (orgData) => {
  const response = await api.post('/organizations/request', orgData);
  return response.data;
};

export const getMyOrganization = async () => {
  const response = await api.get('/organizations/me');
  return response.data;
};

export const updateMyOrganization = async (updates) => {
  const response = await api.put('/organizations/me', updates);
  return response.data;
};

export const getPendingOrganizations = async () => {
  const response = await api.get('/organizations/pending');
  return response.data;
};

export const getAllOrganizations = async (params = {}) => {
  const response = await api.get('/organizations/all', { params });
  return response.data;
};

export const approveOrganization = async (orgId) => {
  const response = await api.post(`/organizations/${orgId}/approve`);
  return response.data;
};

export const rejectOrganization = async (orgId, reason) => {
  const response = await api.post(`/organizations/${orgId}/reject?reason=${encodeURIComponent(reason)}`);
  return response.data;
};

export const getVerifiedOrganizations = async () => {
  const response = await api.get('/organizations/verified/list');
  return response.data;
};

// Analytics Dashboard
export const getAnalyticsDashboard = async () => {
  const response = await api.get('/analytics/dashboard');
  return response.data;
};

export const getCaseConnections = async (limit = 20) => {
  const response = await api.get('/analytics/connections', { params: { limit } });
  return response.data;
};

export const analyzePatterns = async (params = {}) => {
  const response = await api.post('/analytics/ai-patterns', null, { params });
  return response.data;
};

export const getGeographicHeatmap = async () => {
  const response = await api.get('/analytics/geographic-heatmap');
  return response.data;
};

export const exportAnalyticsReport = async (format = 'json') => {
  const response = await api.get('/analytics/export', { params: { format } });
  return response.data;
};

// Share Tracking
export const trackShare = async (caseId, platform) => {
  const response = await publicApi.post(`/cases/${caseId}/share`, null, { 
    params: { platform } 
  });
  return response.data;
};

export const getShareCount = async (caseId) => {
  const response = await publicApi.get(`/cases/${caseId}/share-count`);
  return response.data;
};

export const getShareAnalytics = async (days = 30) => {
  const response = await api.get('/analytics/share-stats', { params: { days } });
  return response.data;
};

// Email Alerts
export const getAlertPreferences = async () => {
  const response = await api.get('/alerts/preferences');
  return response.data;
};

export const updateAlertPreferences = async (preferences) => {
  const response = await api.post('/alerts/preferences', preferences);
  return response.data;
};

export const disableAlerts = async () => {
  const response = await api.delete('/alerts/preferences');
  return response.data;
};

export const sendTestAlert = async () => {
  const response = await api.post('/alerts/test');
  return response.data;
};

// ============ Case Matching API ============

export const getMatchStats = async () => {
  const response = await api.get('/matches/stats');
  return response.data;
};

export const getMatches = async (params = {}) => {
  const response = await api.get('/matches', { params });
  return response.data;
};

// Simple matches endpoint - faster, less enrichment
export const getSimpleMatches = async (params = {}) => {
  const response = await api.get('/matches/simple-matches', { params });
  return response.data;
};

// Attribute-count chips on /potential-matches (Tattoo, Scar, …). Cached
// 15 min on the server so this is cheap to call.
export const getMatchAttributeCounts = async (params = {}) => {
  const response = await api.get('/matches/attribute-counts', { params });
  return response.data;
};

// Single-flight + retry-with-backoff registry so concurrent CaseDetail
// mounts don't fan out into duplicate requests, and intermittent 503s
// during production load-shedding are masked from the UI.
const _matchesInflight = new Map(); // caseId -> Promise

const _sleep = (ms) => new Promise(r => setTimeout(r, ms));

export const getMatchesForCase = async (caseId) => {
  // Coalesce concurrent calls for the same caseId.
  if (_matchesInflight.has(caseId)) {
    return _matchesInflight.get(caseId);
  }

  const exec = (async () => {
    const delays = [200, 500, 1200]; // ms — 4 attempts total (one + three retries)
    let lastErr;
    for (let attempt = 0; attempt <= delays.length; attempt += 1) {
      try {
        const response = await api.get(`/matches/case/${caseId}`);
        return response.data;
      } catch (err) {
        const status = err?.response?.status;
        // Only retry transient server / gateway errors. 4xx (except 429)
        // are user/permission errors — don't paper over them.
        const transient = (
          status === 429 ||
          status === 502 ||
          status === 503 ||
          status === 504 ||
          err?.code === 'ECONNABORTED' ||
          err?.code === 'ERR_NETWORK'
        );
        if (!transient || attempt === delays.length) {
          lastErr = err;
          break;
        }
        // eslint-disable-next-line no-console
        console.warn(`[getMatchesForCase] ${caseId} status=${status || err?.code || 'unknown'} — retry ${attempt + 1}/${delays.length} in ${delays[attempt]}ms`);
        await _sleep(delays[attempt]);
        lastErr = err;
      }
    }
    throw lastErr;
  })();

  _matchesInflight.set(caseId, exec);
  try {
    return await exec;
  } finally {
    _matchesInflight.delete(caseId);
  }
};

export const analyzeMatch = async (matchId) => {
  const response = await api.post(`/matches/${matchId}/analyze`);
  return response.data;
};

export const analyzeMatchPatterns = async (params = {}) => {
  const response = await api.post('/matches/patterns/analyze', null, { params });
  return response.data;
};

export const getMatchRecommendations = async (matchId) => {
  const response = await api.get(`/matches/${matchId}/recommendations`);
  return response.data;
};

// Get plain-English explanation for a match (for families)
export const getMatchExplanation = async (matchId) => {
  const response = await api.get(`/matches/${matchId}/explain`);
  return response.data;
};

// Get AI summary of top matches
export const getTopMatchesSummary = async (params = {}) => {
  const response = await api.get('/matches/top-matches/summary', { params });
  return response.data;
};

// Recalculate match scores with enhanced algorithm
export const recalculateMatchScores = async (params = {}) => {
  const response = await api.post('/matches/recalculate', null, { params });
  return response.data;
};

// Get algorithm info
export const getMatchAlgorithmInfo = async () => {
  const response = await api.get('/matches/algorithm-info');
  return response.data;
};

// Review/classify a match
export const reviewMatch = async (matchId, reviewData) => {
  const response = await api.put(`/matches/${matchId}/review`, reviewData);
  return response.data;
};

// Get reviewed matches
export const getReviewedMatches = async (params = {}) => {
  const response = await api.get('/matches/reviewed', { params });
  return response.data;
};

// Bulk delete matches (admin only)
export const bulkDeleteMatches = async (matchIds) => {
  const response = await api.post('/matches/bulk-delete', matchIds);
  return response.data;
};


// Age Progression AI
export const generateAgeProgression = async (caseId, yearsToAdd = [5, 10, 15]) => {
  const response = await api.post(`/ai/age-progression/${caseId}`, { years_to_add: yearsToAdd });
  return response.data;
};

export const getCachedAgeProgression = async (caseId) => {
  const response = await api.get(`/ai/age-progression/${caseId}`);
  return response.data;
};

// Flag a generated age-progression as inaccurate. Reasons used by the
// "Report incorrect progression" UI:
//   'race_mismatch' | 'skin_tone' | 'other_identity' (clear cache + regen)
//   'incorrect_appearance' (clear cache + regen)
//   'unsure' | 'low_quality' (flag for admin review only)
export const reportAgeProgression = async (caseId, payload) => {
  const response = await api.post(
    `/ai/age-progression/${caseId}/report`,
    { case_id: caseId, ...payload }
  );
  return response.data;
};

// Timeline extraction
export const getCaseTimeline = async (caseId) => {
  const response = await api.get(`/timeline/${caseId}`);
  return response.data;
};

// ==================== ADMINISTRATIVE LINKS ====================
// Record-linkage for unclaimed persons (separate from similarity matching)

export const getAdministrativeLinks = async (params = {}) => {
  const response = await api.get('/administrative-links', { params });
  return response.data;
};

export const getAdminLinksStats = async () => {
  const response = await api.get('/administrative-links/stats');
  return response.data;
};

export const startAdminLinksScan = async (params = {}) => {
  const response = await api.post('/administrative-links/scan', null, { params });
  return response.data;
};

export const getAdminLinksScanStatus = async () => {
  const response = await api.get('/administrative-links/scan-status');
  return response.data;
};

export const reviewAdminLink = async (linkId, status, notes = null) => {
  const params = { status };
  if (notes) params.notes = notes;
  const response = await api.put(`/administrative-links/${linkId}/review`, null, { params });
  return response.data;
};

export const getActionableLinks = async (limit = 20) => {
  const response = await api.get('/administrative-links/actionable', { params: { limit } });
  return response.data;
};

export const testAdminMatch = async (missingPersonId, unclaimedPersonId) => {
  const response = await api.post('/administrative-links/test-match', null, {
    params: { missing_person_id: missingPersonId, unclaimed_person_id: unclaimedPersonId }
  });
  return response.data;
};

// ==================== FACIAL RECOGNITION ====================

export const startFacialEncoding = async (caseType = 'all', batchSize = 50) => {
  const response = await api.post('/facial/encode', null, { 
    params: { case_type: caseType, batch_size: batchSize } 
  });
  return response.data;
};

export const getFacialEncodingStatus = async () => {
  const response = await api.get('/facial/encode/status');
  return response.data;
};

export const stopFacialEncoding = async () => {
  const response = await api.post('/facial/encode/stop');
  return response.data;
};

export const startFacialComparison = async (minSimilarity = 60, batchSize = 100) => {
  const response = await api.post('/facial/compare', null, {
    params: { min_similarity: minSimilarity, batch_size: batchSize }
  });
  return response.data;
};

export const getFacialComparisonStatus = async () => {
  const response = await api.get('/facial/compare/status');
  return response.data;
};

export const stopFacialComparison = async () => {
  const response = await api.post('/facial/compare/stop');
  return response.data;
};

export const getFacialMatches = async (params = {}) => {
  const response = await api.get('/facial/matches', { params });
  return response.data;
};

export const getFacialMatchesStats = async () => {
  const response = await api.get('/facial/matches/stats');
  return response.data;
};

export const reviewFacialMatch = async (matchId, status, notes = null) => {
  const params = { status };
  if (notes) params.notes = notes;
  const response = await api.put(`/facial/matches/${matchId}/review`, null, { params });
  return response.data;
};

export const compareTwoFaces = async (case1Id, case2Id) => {
  const response = await api.post('/facial/compare-two', null, {
    params: { case1_id: case1Id, case2_id: case2Id }
  });
  return response.data;
};

export const getFacialStats = async () => {
  const response = await api.get('/facial/stats');
  return response.data;
};

// Route Planner API functions
export const getUserRoutes = async () => {
  try {
    const response = await api.get('/routes');
    return response.data;
  } catch (error) {
    console.error('Failed to get user routes:', error);
    return [];
  }
};

export const saveUserRoute = async (routeData) => {
  const response = await api.post('/routes', routeData);
  return response.data;
};

export const deleteUserRoute = async (routeId) => {
  await api.delete(`/routes/${routeId}`);
  return true;
};

// Email Subscription API
export const subscribeEmail = async (email, subscriptionType = 'general') => {
  const response = await api.post('/subscribers', { 
    email, 
    subscription_type: subscriptionType 
  });
  return response.data;
};

// Contact Form API
export const submitContactForm = async (formData) => {
  const response = await api.post('/contact', formData);
  return response.data;
};

// ============== GRAPH NETWORK API ==============

// Get graph statistics (admin only)
export const getGraphStats = async () => {
  const response = await api.get('/graph/stats');
  return response.data;
};

// Graph Nodes
export const createGraphNode = async (nodeData) => {
  const response = await api.post('/graph/nodes', nodeData);
  return response.data;
};

export const getGraphNodes = async (params = {}) => {
  const response = await api.get('/graph/nodes', { params });
  return response.data;
};

export const getGraphNode = async (nodeId) => {
  const response = await api.get(`/graph/nodes/${nodeId}`);
  return response.data;
};

export const updateGraphNode = async (nodeId, updateData) => {
  const response = await api.patch(`/graph/nodes/${nodeId}`, updateData);
  return response.data;
};

export const deleteGraphNode = async (nodeId) => {
  const response = await api.delete(`/graph/nodes/${nodeId}`);
  return response.data;
};

// Graph Edges
export const createGraphEdge = async (edgeData) => {
  const response = await api.post('/graph/edges', edgeData);
  return response.data;
};

export const getGraphEdges = async (params = {}) => {
  const response = await api.get('/graph/edges', { params });
  return response.data;
};

export const getGraphEdge = async (edgeId) => {
  const response = await api.get(`/graph/edges/${edgeId}`);
  return response.data;
};

export const updateGraphEdge = async (edgeId, updateData) => {
  const response = await api.patch(`/graph/edges/${edgeId}`, updateData);
  return response.data;
};

export const reviewGraphEdge = async (edgeId, reviewData) => {
  const response = await api.post(`/graph/edges/${edgeId}/review`, reviewData);
  return response.data;
};

export const deleteGraphEdge = async (edgeId) => {
  const response = await api.delete(`/graph/edges/${edgeId}`);
  return response.data;
};

// Network Graph
export const getNetworkGraph = async (nodeId, params = {}) => {
  const response = await api.get(`/graph/network/${nodeId}`, { params });
  return response.data;
};

export const getCaseNetwork = async (caseId, params = {}) => {
  const response = await api.get(`/graph/case/${caseId}/network`, { params });
  return response.data;
};

export const getGraphCaseConnections = async (caseId) => {
  const response = await api.get(`/graph/case/${caseId}/connections`);
  return response.data;
};

// Create node from existing case
export const createNodeFromCase = async (caseId, caseType) => {
  const response = await api.post('/graph/nodes/create-from-case', null, {
    params: { case_id: caseId, case_type: caseType }
  });
  return response.data;
};

// ─── Case Network — case-page convenience helpers ───────────────────────────
// Autocomplete for the AddConnection modal. Searches by name, NamUs ID, or
// LtM internal id. Excludes the current case so it never matches itself.
export const searchCasesForGraph = async (q, { limit = 10, excludeCaseId } = {}) => {
  const params = { q, limit };
  if (excludeCaseId) params.exclude_case_id = excludeCaseId;
  const response = await api.get('/graph/cases/search', { params });
  return response.data;
};

// Create an edge between two cases. Auto-creates graph nodes if missing.
// `body` = { target_case_id, edge_type, label, notes, confidence, status }
export const connectCaseToCase = async (caseId, body) => {
  const response = await api.post(`/graph/cases/${caseId}/connect`, body);
  return response.data;
};

// List named clusters available for the Connect-to-Cluster modal.
export const listGraphClusters = async (q = '') => {
  const response = await api.get('/graph/clusters', { params: q ? { q } : {} });
  return response.data;
};

// Preview the case stubs that belong to a cluster (before bulk-connecting).
// `excludeCaseId` is the current case_id (any form — canonical UUID, NamUs id,
// etc.) — the backend resolves it before filtering it out of the preview.
// `edgeType` enables the backend's already-linked annotation per row.
export const getClusterCases = async (clusterId, { limit = 200, excludeCaseId, edgeType } = {}) => {
  const params = { limit };
  if (excludeCaseId) params.exclude_case_id = excludeCaseId;
  if (edgeType) params.edge_type = edgeType;
  const response = await api.get(`/graph/clusters/${encodeURIComponent(clusterId)}/cases`,
    { params });
  return response.data;
};

// Batch-create same_dump_site edges from this case to every cluster member.
export const connectCaseToCluster = async (caseId, body) => {
  const response = await api.post(`/graph/cases/${caseId}/connect-to-cluster`, body);
  return response.data;
};


export default api;


// -------- Admin Outreach (LE letter dispatch + audit trail) --------
// Tiny in-flight dedup cache. React 18 StrictMode in dev fires every
// useEffect twice on mount, which doubled this heavy list endpoint
// (~100 KB/call, the single-worker backend serializes them — the
// second call blocks the page for ~700 ms while the worker is busy
// with the duplicate first call). Returning the same promise for a
// concurrent identical query collapses the double-fire to a single
// network round-trip without changing call-site semantics.
const __outreachListInflight = new Map();

export const listOutreach = async ({ status = 'all', agency, flag, search, limit = 100, skip = 0 } = {}) => {
  // `flag` is an optional secondary filter (e.g.
  // NEEDS_MANUAL_CANCELLATION_NOTE) that stacks with `status`. Backend
  // validates it as upper-snake-case via pattern; we omit the param
  // entirely when falsy so axios doesn't send `flag=` and trip
  // validation.  `search` is a free-text substring used by the
  // Unmatched Replies picker — case-insensitive match across
  // agency/recipient/subject/case_ids.
  const params = { status, limit, skip };
  if (agency) params.agency = agency;
  if (flag) params.flag = flag;
  if (search) params.search = search;
  const key = JSON.stringify(params);
  if (__outreachListInflight.has(key)) {
    return __outreachListInflight.get(key);
  }
  const p = api.get('/admin/outreach', { params })
    .then((r) => r.data)
    .finally(() => { __outreachListInflight.delete(key); });
  __outreachListInflight.set(key, p);
  return p;
};

export const getOutreachDailyQueue = async () => {
  const response = await api.get('/admin/outreach/daily-queue');
  return response.data;
};

// ── Perpetrator Review Board (Round 6 Action 6e · generalized in Round 8) ──
export const getPerpReviewBoard = async (perpetratorSlug) => {
  const response = await api.get(
    `/admin/outreach/review-board/${perpetratorSlug}`,
  );
  return response.data;
};

export const setPerpReviewBoardDecision = async (
  perpetratorSlug,
  outreachId,
  decision,
  notes = null,
) => {
  const body = { decision };
  if (notes) body.notes = notes;
  const response = await api.post(
    `/admin/outreach/review-board/${perpetratorSlug}/${outreachId}/decision`,
    body,
  );
  return response.data;
};

export const listPerpetratorReviewBoards = async () => {
  const response = await api.get(
    '/admin/outreach/review-board/registry',
  );
  return response.data;
};

// ── Pre-Draft Preview (Round 11 Action 2) ──
export const getPreDraftPreview = async (perpetratorSlug, caseId) => {
  const response = await api.get(
    `/admin/outreach/pre-draft-preview/${perpetratorSlug}/${caseId}`,
  );
  return response.data;
};

export const postParkSweepOnly = async (
  perpetratorSlug, caseId, note = null,
) => {
  const body = {};
  if (note) body.note = note;
  const response = await api.post(
    `/admin/outreach/pre-draft-preview/${perpetratorSlug}/${caseId}/park-sweep-only`,
    body,
  );
  return response.data;
};

// ── Back-compat aliases (Round 6/7 Naso-only names) ──
export const getNasoReviewBoard = async () =>
  getPerpReviewBoard('naso');
export const setNasoReviewBoardDecision = async (
  outreachId, decision, notes = null,
) =>
  setPerpReviewBoardDecision('naso', outreachId, decision, notes);




export const getOutreachDetail = async (outreachId) => {
  const response = await api.get(`/admin/outreach/${outreachId}`);
  return response.data;
};

export const createOutreach = async (payload) => {
  const response = await api.post('/admin/outreach/send', payload);
  return response.data;
};

// Backlog Fix 2 (2026-05-22). Returns the curated list of letter
// starting points (template files + the case-pair generator sentinel)
// the create-outreach dropdown renders. Power-user "Custom path…"
// option is handled client-side; the raw `letter_path` field is shown
// only when that option is selected.
export const fetchOutreachLetterTemplates = async () => {
  const response = await api.get('/admin/outreach/letter-templates');
  return response.data;
};

// 1-click acknowledgment for a `responded` outreach. Creates a reply
// draft pre-filled with one of three canned templates and immediately
// dispatches via Resend. Skips the full reply composer round-trip.
export const sendOutreachStandardAck = async (
  outreachId, { ackKind = 'received', replyToHistoryIndex = null } = {},
) => {
  const body = { ack_kind: ackKind };
  if (replyToHistoryIndex !== null && replyToHistoryIndex !== undefined) {
    body.reply_to_history_index = replyToHistoryIndex;
  }
  const response = await api.post(
    `/admin/outreach/${outreachId}/reply/standard-ack`, body);
  return response.data;
};

// 2026-05-23 — intent-driven letter resolver for the
// /admin/outreach New Outreach modal. Two endpoints:
//   GET  /admin/outreach/outreach-intents       — dropdown options
//   POST /admin/outreach/resolve-template       — (intent, agency)
//                                                  → {letter_path, tone}
// "Custom" is sentinel for "reveal raw path field"; it intentionally
// is NOT POSTable to resolve-template (backend 400's).
export const fetchOutreachIntents = async () => {
  const response = await api.get('/admin/outreach/outreach-intents');
  return response.data;
};

export const resolveOutreachTemplate = async (intent, agencyName) => {
  const response = await api.post('/admin/outreach/resolve-template', {
    intent,
    agency_name: agencyName || null,
  });
  return response.data;
};

// 2026-05-24 — Forward an existing outreach to a new recipient.
// Clones the source row into a new pending_approval record with
// "FW: " subject + same letter content + new recipient. Solves the
// LA County ME forwarding case and every similar situation.
export const forwardOutreach = async (
  outreachId, { recipientEmail, recipientName, agencyName, forwardingNote },
) => {
  const body = { recipient_email: recipientEmail };
  if (recipientName) body.recipient_name = recipientName;
  if (agencyName) body.agency_name = agencyName;
  if (forwardingNote) body.forwarding_note = forwardingNote;
  const response = await api.post(
    `/admin/outreach/${outreachId}/forward`, body);
  return response.data;
};

// 2026-05-24 — Aggregated routing-funnel stats. Powers the
// collapsible "Forwarding Routes" insight panel on /admin/outreach.
export const fetchOutreachRoutingFunnel = async () => {
  const response = await api.get('/admin/outreach/routing-funnel');
  return response.data;
};

// 2026-05-24 — Proactive routing suggestion. Asks the backend if an
// agency has historically forwarded ≥min_count times to the same
// downstream agency; if so, the New Outreach modal surfaces a
// one-line warning + "Switch recipient" button.
//
// Returns: { has_suggestion, destination_agency?, count?,
//            suggested_recipient_email?, latest_forwarded_at?, ... }
export const fetchOutreachRoutingSuggestion = async (agencyName) => {
  if (!agencyName || !agencyName.trim()) {
    return { has_suggestion: false };
  }
  const response = await api.get('/admin/outreach/routing-suggestion', {
    params: { agency_name: agencyName.trim() },
  });
  return response.data;
};

// Letter-file existence pre-flight (Fix 2 — 2026-05-17). Called from
// the Queue-a-New-Outreach modal as the admin types a letter_path.
export const checkOutreachLetterFile = async (letterPath) => {
  const response = await api.get('/admin/outreach/letter-file/exists', {
    params: { letter_path: letterPath },
  });
  return response.data;
};

// Write a new letter markdown file from the inline editor. Refuses
// to overwrite unless `overwrite=true` is passed explicitly.
export const writeOutreachLetterFile = async (letterPath, content, opts = {}) => {
  const response = await api.post('/admin/outreach/letter-file/write', {
    letter_path: letterPath,
    content,
    overwrite: !!opts.overwrite,
  });
  return response.data;
};

// Render a letter from a named template against a (MP, UID) record
// pair and write the result to `letterPath`. Currently the only
// supported template is `case_pair_with_tables` (Mondelli-style
// tables-shaded LEO scaffold). Refuses to write into the
// content/_templates/ subtree and refuses overwrite unless explicit.
export const writeOutreachLetterFromTemplate = async (
  template, mpId, uidId, letterPath, opts = {}) => {
  const response = await api.post(
    '/admin/outreach/letter-file/from-template',
    {
      template,
      mp_id: mpId,
      uid_id: uidId,
      letter_path: letterPath,
      overwrite: !!opts.overwrite,
    },
  );
  return response.data;
};

// Resolve a list of identifier strings (NamUs MP#/UP#, Doe Network
// ID, Charley ID, external_id, or raw UUID) to their canonical
// external_cases records. Used by the create-outreach modal to
// detect "exactly one MP + one UID" pairs and enable the
// case-pair-table template button.
export const resolveOutreachCaseIds = async (caseIds) => {
  const response = await api.post(
    '/admin/outreach/case-ids/resolve',
    { case_ids: caseIds },
  );
  return response.data;
};

export const approveOutreach = async (outreachId, approverNote = '', opts = {}) => {
  const body = { approver_note: approverNote };
  if (opts.gate_override) {
    body.gate_override = true;
    body.gate_override_reason = (opts.gate_override_reason || '').toString();
  }
  const response = await api.post(`/admin/outreach/approve/${outreachId}`, body);
  return response.data;
};

// Run the promotion verification gate against an outreach record.
// Two-layer gate (deterministic mp_uid_exclusions + LLM signal check).
// Returns { verdict: 'CLEAR' | 'NEEDS_REVIEW' | 'BLOCKED', summary,
// sources: string[], layer_a_hits, layer_b_signals }. Persists verdict
// + audit trail to the outreach_logs row.
export const runVerificationGate = async (outreachId) => {
  const response = await api.post(
    `/admin/outreach/${outreachId}/verify`
  );
  return response.data;
};

// Manually transition an outreach log to `sent` — covers physical mail
// dropped at USPS, emails sent outside the system, faxes, and phone-
// confirmed requests. Backend cascades the same case-flag / outreach-
// history side-effects as the automated Resend dispatch path.
//
// Optional `opts.override` (boolean) and `opts.override_reason` (string)
// bypass the held-letter guardrail. Backend rejects with HTTP 409 +
// detail.error='mark_sent_blocked_by_hold' when the letter has any
// HOLD_*, CASEY_REVIEW_BEFORE_SEND, or EXCLUDE_FROM_TODAY_MAIL_RUN flag
// unless override=true AND override_reason are both supplied.
export const markOutreachSent = async (outreachId, payload, opts = {}) => {
  const params = {};
  if (opts.override) {
    params.override = 'true';
    params.override_reason = (opts.override_reason || '').toString();
  }
  const response = await api.post(
    `/admin/outreach/mark-sent/${outreachId}`, payload,
    { params });
  return response.data;
};

export const recordOutreachResponse = async (outreachId, payload) => {
  const response = await api.post(`/admin/outreach/response/${outreachId}`, payload);
  return response.data;
};

export const cancelOutreach = async (outreachId) => {
  const response = await api.delete(`/admin/outreach/${outreachId}`);
  return response.data;
};

// Transition a live outreach log to a `cancelled_*` status with a
// mandatory narrative reason. Backend contract (see
// `routes/admin_outreach.py:CancelWithReasonRequest`):
//   - cancellation_type: one of the 11 whitelisted suffixes
//   - reason: >=20 chars, verbatim into investigator_notes[].text
//   - priority: LOW|NORMAL|HIGH|CRITICAL (default HIGH)
//   - extra_flags: optional additional $addToSet flags
// Prevents the flag-only-no-narrative gap (Ridinger 2026-04-29).
export const cancelOutreachWithReason = async (outreachId, payload) => {
  const response = await api.post(
    `/admin/outreach/${outreachId}/cancel-with-reason`,
    payload,
  );
  return response.data;
};

// Append a narrative `cancellation_amendment` note to an
// already-cancelled record without changing status or flags
// (auto-pulls NEEDS_MANUAL_CANCELLATION_NOTE if it was set, since the
// manual review is now done). Backend contract: see
// `routes/admin_outreach.py:AppendCancellationNoteRequest`.
//   - note: >=20 chars (mirrors CANCELLATION_REASON_MIN_CHARS)
//   - priority: LOW|NORMAL|HIGH|CRITICAL (default HIGH)
export const appendCancellationNote = async (outreachId, payload) => {
  const response = await api.post(
    `/admin/outreach/${outreachId}/append-cancellation-note`,
    payload,
  );
  return response.data;
};

// Mirrors `_ALLOWED_CANCELLATION_TYPES` in the backend. When adding
// a new cancellation type, update BOTH this list and the backend
// whitelist — they must stay in lockstep or pydantic will reject.
export const CANCELLATION_TYPES = [
  { value: 'match_excluded',
    label: 'Match excluded — UID/MP cross-reference invalidated' },
  { value: 'duplicate',
    label: 'Duplicate — another outreach covers this agency + case' },
  { value: 'identification_confirmed',
    label: 'Identification confirmed — case solved before we mailed' },
  { value: 'mo_exclusion',
    label: 'MO exclusion — perpetrator MO rules out the pairing' },
  { value: 'dna_exclusion',
    label: 'DNA exclusion — DNA result rules out the pairing' },
  { value: 'agency_declined',
    label: 'Agency declined — agency asked us to stand down' },
  { value: 'pending_verification',
    label: 'Pending verification — blocked on ID-status verification' },
  { value: 'out_of_scope',
    label: 'Out of scope — wrong jurisdiction / agency' },
  { value: 'superseded',
    label: 'Superseded — newer outreach replaces this one' },
  { value: 'stale',
    label: 'Stale — queue-cleanup of old auto-draft' },
  { value: 'other',
    label: 'Other (catch-all; narrative required)' },
];
export const CANCELLATION_REASON_MIN_CHARS = 20;

// Fetch the printable PDF letterhead render (with logo + letterhead) for an
// outreach record. Returns a Blob for `URL.createObjectURL`.
export const fetchOutreachPdfBlob = async (outreachId) => {
  const response = await api.get(`/admin/outreach/${outreachId}/pdf`, {
    responseType: 'blob',
  });
  return response.data;
};

// Fetch the FOIA / Physical Mail Tracking Log PDF — one-page landscape
// sheet listing every `outreach_logs` record whose `dispatch_channel`
// starts with `physical_mail` (canonical value is
// `physical_mail_primary` after the April 2026 channel unification;
// `^physical_mail` prefix kept for historical safety). Regenerated
// server-side on every call. Returns a Blob.
export const fetchFoiaTrackingLogPdfBlob = async () => {
  const response = await api.get('/admin/outreach/tracking-log.pdf', {
    responseType: 'blob',
  });
  return response.data;
};

// Admin: persist physical-mail tracking fields (Date Mailed, USPS #,
// Response Received, Response Date) on an outreach_logs record. The
// server stores under `outreach_logs.tracking.*` and rejects the call
// (400) if the record's `dispatch_channel` isn't a physical_mail
// variant. All payload fields are optional — supports partial updates.
export const updateOutreachTracking = async (outreachId, payload) => {
  const response = await api.post(
    `/admin/outreach/${outreachId}/tracking`, payload);
  return response.data;
};

// Admin: mark a responded outreach record as terminal `closed`. The
// server requires a closure_note (3+ chars). Returns the updated
// record. Closed records are read-only on subsequent edits.
export const closeOutreach = async (outreachId, closureNote) => {
  const response = await api.post(
    `/admin/outreach/${outreachId}/close`,
    { closure_note: closureNote },
  );
  return response.data;
};

// Admin: edit recipient block + dispatch channel on a pending_approval
// outreach record. The server re-renders the cached PDF if one is on
// file. All payload fields optional — supports partial updates.
// Payload shape:
//   { recipient_name?, recipient_unit?, agency_name?,
//     recipient_mailing_address?, recipient_email?, recipient_phone?,
//     cc_email?, dispatch_channel?, mail_only_no_email? }
export const patchOutreachRecipient = async (outreachId, payload) => {
  const response = await api.patch(
    `/admin/outreach/${outreachId}/recipient`, payload);
  return response.data;
};

// Dual-Dispatch: manually fire a courtesy CC email of the letter to
// the configured `cc_email` address. Only valid when the outreach
// record is already in status sent/responded/responded_replied and a
// cc_email is set. Increments `cc_dispatch_count` server-side so the
// admin can re-send (e.g. after a bounce) and the audit trail stays
// honest about how many copies went out.
export const sendOutreachCcEmail = async (outreachId) => {
  const response = await api.post(
    `/admin/outreach/${outreachId}/send-cc`);
  return response.data;
};

// Fetch the phone-follow-up call sheet PDF (1 page, portrait) — case
// + match context, agency phone, 3-bullet ask, write-in log fields.
// Available for any outreach regardless of dispatch_channel.
export const fetchOutreachCallSheetPdfBlob = async (outreachId) => {
  const response = await api.get(
    `/admin/outreach/${outreachId}/call-sheet.pdf`, {
      responseType: 'blob',
    });
  return response.data;
};

// Draft a follow-up letter for an overdue physical-mail outreach.
// Server-side gated: rejects non-physical_mail records, un-mailed
// records, records not yet 30+ days old, and records already
// response_received=true. Returns `{status: drafted|already_exists,
// follow_up_id, parent_id, letter_filename?}`. Parent record's
// `follow_ups[]` list is updated atomically.
export const createOutreachFollowUp = async (outreachId) => {
  const response = await api.post(
    `/admin/outreach/${outreachId}/follow-up`);
  return response.data;
};

// ─── Reply-to-Agency on responded outreach records ─────────────────
// Pre-fills recipient, subject ("Re: " + inbound subject), threading
// headers (In-Reply-To + References from the inbound Message-ID), and
// a quoted-stub body. The draft is editable via updateOutreachReply
// before being dispatched via sendOutreachReply.
export const createOutreachReplyDraft = async (
  outreachId,
  { replyToHistoryIndex = null } = {},
) => {
  const body = {};
  if (replyToHistoryIndex !== null && replyToHistoryIndex !== undefined) {
    body.reply_to_history_index = replyToHistoryIndex;
  }
  const response = await api.post(
    `/admin/outreach/${outreachId}/reply/draft`, body);
  return response.data;
};

export const updateOutreachReply = async (outreachId, replyId, fields) => {
  const response = await api.patch(
    `/admin/outreach/${outreachId}/reply/${replyId}`, fields);
  return response.data;
};

export const deleteOutreachReply = async (outreachId, replyId) => {
  const response = await api.delete(
    `/admin/outreach/${outreachId}/reply/${replyId}`);
  return response.data;
};

export const sendOutreachReply = async (outreachId, replyId) => {
  // Dispatch path includes a Resend API call which can take 20–60 s
  // under load + N parallel DB writes for case-linked notes. The
  // default 45 s axios timeout occasionally fired AFTER the reply
  // had landed server-side, leaving the UI showing a "timeout"
  // toast for a reply that actually sent. Allow up to 3 minutes
  // here; the route logs each phase's elapsed time so we can
  // diagnose persistent slowness from server logs.
  const response = await api.post(
    `/admin/outreach/${outreachId}/reply/${replyId}/send`,
    {},
    { timeout: 180000 },
  );
  return response.data;
};

// Toggle the `ready_to_dispatch` sub-status on a pending_approval
// outreach record. Pure presentational sub-status used to surface
// "reviewed and ready" letters above unreviewed drafts in the queue.
export const setOutreachReadyToDispatch = async (outreachId, ready) => {
  const response = await api.post(
    `/admin/outreach/${outreachId}/ready-to-dispatch`, { ready });
  return response.data;
};

// Append an admin-authored investigator note to a case. Writes BOTH
// legacy (`type/text/timestamp`) and 2026-04 (`kind/body/created_at`)
// field pairs so legacy scanner scripts and the new UI both render
// correctly. Returns `{status, case_id, note}`.
export const addCaseInvestigatorNote = async (caseId, payload) => {
  const response = await api.post(
    `/admin/cases/${caseId}/investigator-notes`, payload);
  return response.data;
};

// Admin: fetch the enrichment-flag curation dashboard data. When
// `flag` is set, `cases` is populated with the up-to-limit matching
// records (each enriched with top_match_score + latest_note snippet).
// When `flag` is null/undefined, only `flag_counts` is returned so
// the sidebar can render cheaply.
export const fetchAdminAnnotations = async ({ flag, q, limit = 200 } = {}) => {
  const params = {};
  if (flag) params.flag = flag;
  if (q) params.q = q;
  if (limit) params.limit = limit;
  const response = await api.get('/admin/annotations', { params });
  return response.data;
};

// Admin: list of MO Exclusion Cascade types for the dropdown menu.
// Returns `{ types: [{ key, label, marker_flag }, …] }`.
export const fetchMOExclusionTypes = async () => {
  const response = await api.get('/admin/mo-exclusion/types');
  return response.data;
};

// Admin: apply MO Exclusion Cascade to a case. Strips every
// perpetrator flag whose documented MO is incompatible with the
// chosen exclusion type, adds the corresponding MOD_EXCLUSION_*
// marker, and writes a single unified investigator note. Idempotent.
// Returns `{ status, case_id, exclusion_type, marker_flag,
// stripped_flags, stripped_count, rationale, note }`.
export const applyMOExclusionCascade = async (caseId, exclusionType) => {
  const response = await api.post(
    `/admin/cases/${caseId}/mo-exclusion`,
    { exclusion_type: exclusionType },
  );
  return response.data;
};

// Admin: remove PODCAST_MATCH_NEEDS_REVIEW from a case after manual review.
// Appends an audit-trail entry server-side with the admin's email + timestamp.
export const clearPodcastReviewFlag = async (caseId) => {
  const response = await api.post(
    `/admin/cases/${caseId}/clear-podcast-review`);
  return response.data;
};

// -------- Case flag editor (admin) --------
// Append a flag to `case.flags[]`. Idempotent server-side via $addToSet;
// returns `{status, case_id, flag, flags, audit}` where `flags` is the
// post-update list so the caller can re-render without a second GET.
export const addCaseFlag = async (caseId, flag) => {
  const response = await api.post(
    `/admin/cases/${caseId}/flags`, { flag });
  return response.data;
};

// Remove a flag from `case.flags[]`. 404 when the flag wasn't on the
// case so the UI can distinguish "you typo'd a value" from a real
// no-op. Returns the post-update flag list.
export const removeCaseFlag = async (caseId, flag) => {
  const response = await api.delete(
    `/admin/cases/${caseId}/flags/${encodeURIComponent(flag)}`);
  return response.data;
};

// Autocomplete flag values against the corpus (distinct values across
// missing_persons + external_cases + canonical_cases). Returns
// `{flags: string[], total_distinct: number}`. Prefix matches rank
// before substring matches.
export const fetchFlagAutocomplete = async (q = '', limit = 20) => {
  const params = { limit };
  if (q) params.q = q;
  const response = await api.get('/admin/flags/autocomplete', { params });
  return response.data;
};


// Admin: fetch every case currently flagged PODCAST_MATCH_NEEDS_REVIEW along
// with the episode title + URL + top match score we need to render the
// reviewer table.
export const listPodcastNeedsReview = async () => {
  const response = await api.get('/admin/podcast-needs-review');
  return response.data;
};


// -------- Partial-remains-identified review queue (admin) --------
export const listPartialRemainsReview = async () => {
  const response = await api.get('/admin/partial-remains-review');
  return response.data;
};

export const excludePartialRemainsCase = async (caseId) => {
  const response = await api.post(
    `/admin/partial-remains-review/${caseId}/exclude`
  );
  return response.data;
};

export const keepActivePartialRemainsCase = async (caseId) => {
  const response = await api.post(
    `/admin/partial-remains-review/${caseId}/keep-active`
  );
  return response.data;
};

// -------- Doe Network weekly crawler stats (admin widget) --------
export const getDoeCrawlStats = async () => {
  const response = await api.get('/admin/outreach/doe-crawl/stats');
  return response.data;
};

// -------- Weekly removal-check status (admin widget) --------
export const getRemovalCheckStatus = async () => {
  const response = await api.get(
    '/admin/outreach/removal-check/status'
  );
  return response.data;
};

// -------- Unmatched Resend inbound replies --------
export const listUnmatchedReplies = async ({ limit = 50, includeResolved = false } = {}) => {
  const response = await api.get('/webhooks/resend/unmatched', {
    params: { limit, include_resolved: includeResolved }
  });
  return response.data;
};

export const resolveUnmatchedReply = async (resendInboundId, outreachId, note = '') => {
  const response = await api.post(
    `/webhooks/resend/unmatched/${resendInboundId}/resolve`,
    { outreach_id: outreachId, note }
  );
  return response.data;
};

// 2026-06-01 — Best-guess outreach for an unmatched reply. Drives
// the one-tap "Link to <agency>" button in the full-message reader
// dialog. Returns {top_match, runners_up, has_high_confidence}.
export const fetchUnmatchedReplyBestGuess = async (resendInboundId) => {
  const response = await api.get(
    `/webhooks/resend/unmatched/${resendInboundId}/best-guess`);
  return response.data;
};

export const dismissUnmatchedReply = async (resendInboundId, reason = '') => {
  const response = await api.post(
    `/webhooks/resend/unmatched/${resendInboundId}/dismiss`,
    { reason }
  );
  return response.data;
};

// 2026-06-10 — Send a caseless reply to an unmatched inbound email.
// Creates a case_ids=[] outreach_logs record and dispatches via
// Resend with RFC-5322 threading headers so the recipient's reply
// threads back to the same conversation.
export const replyToUnmatchedReply = async (resendInboundId, {
  body_markdown,
  subject = null,
  to_email = null,
  to_name = null,
  cc_emails = null,
  bcc_emails = null,
  tags = null,
  note = null,
} = {}) => {
  const response = await api.post(
    `/webhooks/resend/unmatched/${resendInboundId}/reply`,
    { body_markdown, subject, to_email, to_name, cc_emails, bcc_emails, tags, note }
  );
  return response.data;
};

// 2026-06-10 — Triage action: mark unmatched inbound as legitimate
// standalone correspondence (not case-related, not spam). Drops from
// the queue without linking to any outreach.
export const markUnmatchedAsGeneralCorrespondence = async (resendInboundId, note = '') => {
  const response = await api.post(
    `/webhooks/resend/unmatched/${resendInboundId}/mark-general-correspondence`,
    { note }
  );
  return response.data;
};

/**
 * Download a persisted inbound attachment as a Blob. Returns
 * `{ blob, filename }` so the caller can trigger a browser save.
 * Uses the authenticated `api` instance so the admin-only download
 * route's Bearer token + cookie are sent.
 */
export const downloadInboundAttachment = async (emailId, attachmentId) => {
  const response = await api.get(
    `/webhooks/resend/attachments/${emailId}/${attachmentId}`,
    { responseType: 'blob' }
  );
  // Try to recover the filename from Content-Disposition
  const cd = response.headers?.['content-disposition'] || '';
  let filename = `${attachmentId}.bin`;
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  if (m) filename = decodeURIComponent(m[1]);
  return { blob: response.data, filename };
};

/**
 * Trigger a retroactive Resend attachment fetch for an inbound reply
 * whose `attachments[]` is empty (logged before Gap A landed).
 */
/**
 * Admin Manual Case Search — POST /api/admin/manual-search.
 * Returns { total, truncated, anchor_uid_resolved, results: [...] }
 */
export const runManualCaseSearch = async (filters) => {
  const response = await api.post('/admin/manual-search', filters);
  return response.data;
};

/**
 * Admin Manual Case Search — CSV export. Returns { blob, filename }
 * so the caller can trigger a browser save.
 */
export const exportManualCaseSearch = async (filters) => {
  const response = await api.post(
    '/admin/manual-search/export',
    filters,
    { responseType: 'blob' },
  );
  const cd = response.headers?.['content-disposition'] || '';
  let filename = 'manual_search.csv';
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  if (m) filename = decodeURIComponent(m[1]);
  return { blob: response.data, filename };
};

export const refetchUnmatchedAttachments = async (resendInboundId) => {
  const response = await api.post(
    `/webhooks/resend/unmatched/${resendInboundId}/refetch-attachments`
  );
  return response.data;
};



// ============================================================
// Community Workbench (Reddit launch — research notes, flags, /workbench)
// ============================================================
export const listCommunityNotes = async (caseId) => {
  const response = await api.get(`/community/notes/${encodeURIComponent(caseId)}`);
  return response.data;
};

export const createCommunityNote = async (caseId, text) => {
  const response = await api.post('/community/notes', { case_id: caseId, text });
  return response.data;
};

export const deleteCommunityNote = async (noteId) => {
  const response = await api.delete(`/community/notes/${noteId}`);
  return response.data;
};

export const flagMatch = async (matchId, reason) => {
  const response = await api.post('/community/flag-match', { match_id: matchId, reason });
  return response.data;
};

export const getWorkbenchFeed = async () => {
  const response = await api.get('/community/workbench-feed');
  return response.data;
};

export const listFlaggedMatches = async (status) => {
  const params = status ? { status } : {};
  const response = await api.get('/admin/flagged-matches', { params });
  return response.data;
};

export const approveFlaggedMatch = async (flagId) => {
  const response = await api.post(`/admin/flagged-matches/${flagId}/approve`);
  return response.data;
};

export const dismissFlaggedMatch = async (flagId) => {
  const response = await api.post(`/admin/flagged-matches/${flagId}/dismiss`);
  return response.data;
};


// ──────────────────────────────────────────────────────────────
// Direct Email Composer (no case IDs)
// ──────────────────────────────────────────────────────────────
// Mounted under the same outreach prefix so direct emails appear
// alongside case-based outreach in the queue (tagged DIRECT_EMAIL).
// Two-step gate identical to case-based: create -> pending_approval,
// then explicit /send call to dispatch via Resend. See
// /app/backend/routes/admin_outreach_direct_email.py.

export const directEmailDraft = async ({
  prompt, bullets, tone, recipient_org,
}) => {
  // AI-drafted body via Claude. Read-only — returns proposed subject
  // + body, does NOT save. 10-30 s typical. Uses the keepalive
  // streaming helper so it never trips the proxy at 60 s.
  const response = await api.post('/admin/outreach/direct-email/draft', {
    prompt, bullets, tone, recipient_org,
  }, { timeout: 180000 });  // 3-minute ceiling for AI draft
  return response.data;
};

export const directEmailCreate = async (payload) => {
  // Creates a pending_approval direct-email record. NOT yet
  // dispatched. Caller follows up with directEmailSend after the
  // user clicks "Send" in the queue UI.
  const response = await api.post('/admin/outreach/direct-email', payload);
  return response.data;
};

export const directEmailSend = async (outreachId, approverNote) => {
  // Approve + dispatch via Resend. Body sanitisers re-run on send
  // (defence-in-depth). Mirrors approve_and_send for case-based
  // outreach but skips the verification + table-quality gates
  // (irrelevant for direct emails — no case_ids, no tables).
  const response = await api.post(
    `/admin/outreach/direct-email/${outreachId}/send`,
    { approver_note: approverNote },
  );
  return response.data;
};
